import axios from 'axios';

export async function getPrivacyMe() {
  const { data } = await axios.get('/api/privacy/me');
  return data;
}

const acceptanceByUser = new Map();

export function privacyAcceptanceUserKey(user) {
  if (!user || user.role === 'guest' || user.role === 'admin') return '';
  return String(user.email || user.id || user._id || '').trim().toLowerCase();
}

export function readCachedPrivacyAcceptance(userKey) {
  if (!userKey) return null;
  return acceptanceByUser.get(userKey) || null;
}

/** Call after a successful accept so the next page does not wait on another privacy read. */
export function notePrivacyAccepted(userKey) {
  if (!userKey) return;
  acceptanceByUser.set(userKey, { acceptanceRequired: false, reAcceptanceRequired: false });
}

export function clearPrivacyAcceptanceCache(userKey) {
  if (userKey) acceptanceByUser.delete(userKey);
  else acceptanceByUser.clear();
}

export async function getPrivacyAcceptance(userKey) {
  const cached = readCachedPrivacyAcceptance(userKey);
  if (cached && !cached.acceptanceRequired && !cached.reAcceptanceRequired) return cached;
  const { data } = await axios.get('/api/privacy/me/acceptance');
  if (userKey && !data?.acceptanceRequired && !data?.reAcceptanceRequired) {
    acceptanceByUser.set(userKey, data);
  }
  return data;
}

export async function getPrivacyHistory({ page = 1, limit = 20 } = {}) {
  const { data } = await axios.get('/api/privacy/me/history', { params: { page, limit } });
  return data;
}

export async function listPrivacyDocuments(kind = 'acceptance') {
  const { data } = await axios.get('/api/privacy/documents', {
    params: kind === 'acceptance' ? undefined : { kind },
  });
  return data;
}

export async function listPublicLegalDocuments() {
  const { data } = await axios.get('/api/privacy/documents/public');
  return data;
}

export async function postPrivacyEvent(payload) {
  const { data } = await axios.post('/api/privacy/event', payload);
  return data;
}

export async function acceptLegalDocument(documentKey, version) {
  const { data } = await axios.post('/api/privacy/legal/accept', { documentKey, version });
  return data;
}

export async function getPrivacyMeData() {
  const { data } = await axios.get('/api/privacy/me/data');
  return data;
}

export async function patchPrivacyMeProfile(payload) {
  const { data } = await axios.patch('/api/privacy/me/profile', payload);
  return data;
}

export async function erasePrivacyMeData(confirmEmail) {
  const { data } = await axios.post('/api/privacy/me/erase', { confirmEmail });
  return data;
}

export async function recordPrivacyExport() {
  const { data } = await axios.post('/api/privacy/me/export');
  return data;
}

export async function postPrivacyGrievance({ subject, message }) {
  const { data } = await axios.post('/api/privacy/me/grievance', { subject, message });
  return data;
}

export async function listLegalAcceptances(params = {}) {
  const { data } = await axios.get('/api/admin/privacy/acceptances', { params });
  return data;
}

export async function listErasureRequests() {
  const { data } = await axios.get('/api/admin/privacy/erasure-requests');
  return data;
}

export async function listAdminDocuments(params = {}) {
  const { data } = await axios.get('/api/admin/privacy/documents', { params });
  return data;
}

export async function createAdminDocument(payload) {
  const { data } = await axios.post('/api/admin/privacy/documents', payload);
  return data;
}

export async function updateAdminDocument(id, payload) {
  const { data } = await axios.patch(`/api/admin/privacy/documents/${id}`, payload);
  return data;
}

export async function publishAdminDocument(id, requiresReAcceptance) {
  const { data } = await axios.post(`/api/admin/privacy/documents/${id}/publish`, {
    requiresReAcceptance,
  });
  return data;
}

export async function retireAdminDocument(id) {
  const { data } = await axios.post(`/api/admin/privacy/documents/${id}/retire`);
  return data;
}

export async function lookupAdminPrincipal({ principalId, email } = {}) {
  if (principalId) {
    const { data } = await axios.get(`/api/admin/privacy/principal/${principalId}`, {
      params: email ? { email } : undefined,
    });
    return data;
  }
  const { data } = await axios.get('/api/admin/privacy/principal', { params: { email } });
  return data;
}
